import React, { useCallback, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Formik } from 'formik';
import { Box, Button, FormControl, TextField } from '@mui/material';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { selectDistributorDetail } from 'src/store/selectors/distributorSelectors';
import DropdownMenu from 'src/components/DropdownMenu/DropdownMenu';
import {
  DaysForFirstRentAfterContractStartList,
  getTermListForQuoteAndContracts,
  msiTermList
} from 'src/utils/constants/distributors';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'src/utils/dayjs';
import momentLocalTimezone from 'src/utils/momentLocalTimezone';
import { adjustTo25IfTodayIsAfter25 } from 'src/utils/contracts';
import Autocomplete from 'src/components/Autocomplete/Autocomplete';
import { selectClientsList } from 'src/store/selectors/clientSelectors';
import { isLoadingSelector } from 'src/store/selectors/statusSelector';
import { LOAD_CLIENTS, loadAllClients } from 'src/store/actions/clientActions';
import getValidationSchema from './schema';
import makeInitialState from '../../initialState';
import useStyles from '../styles';

const StepOne = ({ data, handleNext }) => {
  const { classes } = useStyles();
  const initialState = useMemo(() => {
    return {
      ...makeInitialState(data)
    };
  }, [data]);
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const distributorParatemers = useSelector(selectDistributorDetail);
  const clientList = useSelector(selectClientsList);
  const isLoadingClients = useSelector(
    isLoadingSelector([LOAD_CLIENTS.action])
  );

  const onNext = useCallback(
    async values => {
      handleNext({ ...data, ...values });
    },
    [handleNext]
  );

  const termList = useMemo(() => {
    if (data.isMsiModality && data.selectedTerms?.length) {
      return msiTermList.filter(item => data.selectedTerms.includes(item.key));
    }
    return getTermListForQuoteAndContracts(
      distributorParatemers,
      data.isMsiModality
    );
  }, [distributorParatemers, data]);

  useEffect(() => {
    dispatch(loadAllClients());
  }, []);

  return (
    <>
      <Formik
        initialValues={initialState}
        enableReinitialize
        validationSchema={getValidationSchema}
        onSubmit={onNext}
      >
        {({
          errors,
          handleSubmit,
          touched,
          values,
          setFieldValue,
          handleChange,
          handleBlur
        }) => {
          return (
            <form onSubmit={handleSubmit}>
              <Box className={classes.container}>
                <FormControl
                  fullWidth
                  error={Boolean(touched.clientId && errors.clientId)}
                >
                  <Autocomplete
                    options={clientList}
                    id="clientId"
                    label={t('COMMON.CLIENT')}
                    isLoading={isLoadingClients}
                    getOptionLabel={option => {
                      return option.fullName
                        ? `${option.fullName} - ID:${option.id}`
                        : '';
                    }}
                    value={clientList.find(
                      client => client.id === values.clientId
                    )}
                    fullWidth
                    sx={{ marginBottom: 2, marginTop: 2 }}
                    onChange={(_e, value) => {
                      setFieldValue('clientId', value?.id ?? '');
                    }}
                    helperText={touched.clientId && t(errors.clientId)}
                    error={Boolean(touched.clientId && errors.clientId)}
                  />
                </FormControl>

                <DropdownMenu
                  onBlur={handleBlur}
                  label={t('COMMON.TERM')}
                  value={values.term}
                  name="term"
                  setValue={value => {
                    setFieldValue('term', value);
                  }}
                  error={Boolean(touched.term && errors.term)}
                  errorText={touched.term && t(errors.term)}
                  list={termList}
                />
                <DropdownMenu
                  list={DaysForFirstRentAfterContractStartList}
                  onBlur={handleBlur}
                  label={t('COMMON.DAYS_FOR_FIRST_RENT')}
                  disabled={data.gracePeriod}
                  value={values.gracePeriod}
                  name="gracePeriod"
                  setValue={value => {
                    setFieldValue('gracePeriod', value);
                    setFieldValue(
                      'firstRentDate',
                      dayjs(values.initialPaymentDate).add(value + 30, 'days')
                    );
                  }}
                  error={Boolean(touched.gracePeriod && errors.gracePeriod)}
                  errorText={touched.gracePeriod && t(errors.gracePeriod)}
                />

                <DatePicker
                  label={t('CONTRACT_VIEW.INITIAL_PAYMENT_DATE')}
                  format="DD/MM/YYYY"
                  disabled={data.initialPaymentDate}
                  value={values.initialPaymentDate}
                  minDate={dayjs.tz()}
                  onChange={newValue => {
                    setFieldValue('initialPaymentDate', dayjs(newValue));
                  }}
                  slotProps={{
                    textField: {
                      sx: { width: '100%', mt: 2, mb: 1 },
                      error: Boolean(touched.initialPaymentDate && errors.initialPaymentDate),
                      helperText: touched.initialPaymentDate && t(errors.initialPaymentDate),
                      FormHelperTextProps: { sx: { color: 'red' } },
                      InputProps: { autoComplete: 'off' }
                    }
                  }}
                />

                <DatePicker
                  label={t('CONTRACT_VIEW.FIRST_RENT_DATE')}
                  format="DD/MM/YYYY"
                  disabled={data.firstRentDate}
                  value={values.firstRentDate}
                  minDate={adjustTo25IfTodayIsAfter25(
                    values.initialPaymentDate
                      ? momentLocalTimezone(values.initialPaymentDate)
                      : momentLocalTimezone()
                  )}
                  onChange={newValue => {
                    setFieldValue(
                      'firstRentDate',
                      momentLocalTimezone(newValue)
                    );
                  }}
                  slotProps={{
                    textField: {
                      sx: { width: '100%', mt: 2, mb: 1 },
                      error: Boolean(touched.firstRentDate && errors.firstRentDate),
                      helperText: touched.firstRentDate && t(errors.firstRentDate),
                      FormHelperTextProps: { sx: { color: 'red' } },
                      InputProps: { autoComplete: 'off' }
                    }
                  }}
                />

                <TextField
                  error={Boolean(touched.moreInfo && errors.moreInfo)}
                  fullWidth
                  helperText={touched.moreInfo && t(errors.moreInfo)}
                  label={t('COMMON.MORE_EQUIPMENT_INFO')}
                  name="moreInfo"
                  onBlur={handleBlur}
                  onChange={handleChange}
                  multiline
                  margin="normal"
                  type="text"
                  maxRows={4}
                  value={values.moreInfo}
                  variant="outlined"
                  inputProps={{ maxLength: 120, autoComplete: 'off' }}
                />
              </Box>
              <Box
                className={classes.box}
                display="flex"
                justifyContent="flex-end"
              >
                <Button
                  className={clsx(classes.buttons, classes.buttonMain)}
                  type="submit"
                  color="primary"
                  variant="outlined"
                >
                  {t('COMMON.NEXT')}
                </Button>
              </Box>
            </form>
          );
        }}
      </Formik>
    </>
  );
};
StepOne.propTypes = {
  data: PropTypes.object,
  handleNext: PropTypes.func
};

export default StepOne;
